import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { RotateCcw, IndianRupee, Percent } from "lucide-react";

const DEFAULTS = {
  platform_fee: 0,
  slab1_threshold: 1000,
  slab1_pct: 18,
  slab2_pct: 20,
  gst_on_commission: false,
  gst_pct_on_commission: 18,
};

export default function CommissionFeesTab() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(1500);

  const load = async () => {
    try {
      const { data } = await api.get("/commission-config");
      setCfg(data); setForm(data);
    } catch { toast.error("Could not load commission config"); }
  };

  useEffect(() => { load(); }, []);

  if (!form) return <div className="text-sm text-gray-500 py-10 text-center">Loading fees…</div>;

  const dirty = ["platform_fee", "slab1_threshold", "slab1_pct", "slab2_pct", "gst_on_commission"]
    .some((k) => String(form[k]) !== String(cfg?.[k]));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = {
      platform_fee: Number(form.platform_fee),
      slab1_threshold: Number(form.slab1_threshold),
      slab1_pct: Number(form.slab1_pct),
      slab2_pct: Number(form.slab2_pct),
      gst_on_commission: !!form.gst_on_commission,
    };
    if ([payload.platform_fee, payload.slab1_threshold, payload.slab1_pct, payload.slab2_pct].some((n) => Number.isNaN(n))) {
      return toast.error("Please enter valid numbers");
    }
    if (payload.slab1_threshold <= 0) return toast.error("Slab 1 threshold must be greater than zero");
    if (payload.slab1_pct < 0 || payload.slab1_pct > 100) return toast.error("Slab 1 percentage must be 0–100");
    if (payload.slab2_pct < 0 || payload.slab2_pct > 100) return toast.error("Slab 2 percentage must be 0–100");
    if (payload.platform_fee < 0) return toast.error("Platform fee cannot be negative");
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/commission-config", payload);
      setCfg(data); setForm(data);
      toast.success("Commission settings saved. New payouts will use these rates.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save changes"); }
    finally { setBusy(false); }
  };

  const resetDefaults = () => {
    if (!window.confirm("Reset all commission settings to defaults (0 fee, 18%/20% slabs, GST off)?")) return;
    setForm({ ...form, ...DEFAULTS });
  };

  // Live preview using current form values
  const g = Number(preview) || 0;
  const pct = g <= Number(form.slab1_threshold) ? Number(form.slab1_pct) : Number(form.slab2_pct);
  const commission = +(g * pct / 100).toFixed(2);
  const gst = form.gst_on_commission ? +(commission * Number(form.gst_pct_on_commission || 18) / 100).toFixed(2) : 0;
  const platformFee = Number(form.platform_fee) || 0;
  // Platform fee is billed to the buyer at checkout, not deducted from the
  // vendor. Vendor take-home is only reduced by commission + GST on commission.
  const deduction = +(commission + gst).toFixed(2);
  const net = +(g - deduction).toFixed(2);

  const NumField = ({ id, label, prefix, value, onChange, hint, step = "0.01", min = "0" }) => (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="relative mt-1">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{prefix}</span>}
        <Input
          id={id}
          data-testid={id}
          type="number"
          step={step}
          min={min}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={prefix ? "pl-7" : ""}
        />
      </div>
      {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="fees-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-display text-2xl">Commission &amp; Fees</div>
          <div className="text-xs text-gray-500 mt-1">Every vendor payout — weekly settlement, GST reports, and payout policy page — recalculates from these values the moment you save.</div>
        </div>
        <Button data-testid="fees-reset" onClick={resetDefaults} variant="outline" className="rounded-full">
          <RotateCcw className="h-3.5 w-3.5 mr-2" /> Reset to defaults
        </Button>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div className="p-5 rounded-2xl bg-white border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <IndianRupee className="h-4 w-4 text-[#F48E2E]" />
            <div className="font-medium">Platform Fee</div>
          </div>
          <NumField
            id="platform-fee"
            label="Flat fee per order"
            prefix="₹"
            value={form.platform_fee}
            onChange={(v) => set("platform_fee", v)}
            hint="Charged to the buyer at checkout as a separate line item. Not deducted from vendor payouts. Set to 0 to disable."
          />
        </div>

        <div className="p-5 rounded-2xl bg-white border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <Percent className="h-4 w-4 text-[#F48E2E]" />
            <div className="font-medium">GST on Commission</div>
          </div>
          <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
            <div className="text-sm">
              <div className="font-medium">Add GST on commission</div>
              <div className="text-xs text-gray-500">When on, GST at {form.gst_pct_on_commission || 18}% is added on top of the commission amount.</div>
            </div>
            <Switch
              data-testid="gst-on-commission"
              checked={!!form.gst_on_commission}
              onCheckedChange={(v) => set("gst_on_commission", !!v)}
            />
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="font-medium">Commission Slab 1</div>
            <span className="text-[11px] uppercase tracking-widest text-gray-500">Low value</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NumField
              id="slab1-threshold"
              label="Applies when order gross ≤"
              prefix="₹"
              value={form.slab1_threshold}
              onChange={(v) => set("slab1_threshold", v)}
              step="1"
              min="1"
            />
            <NumField
              id="slab1-pct"
              label="Commission %"
              value={form.slab1_pct}
              onChange={(v) => set("slab1_pct", v)}
              step="0.1"
              min="0"
            />
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="font-medium">Commission Slab 2</div>
            <span className="text-[11px] uppercase tracking-widest text-gray-500">High value</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Applies when order gross &gt;</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
                <Input value={form.slab1_threshold} disabled className="pl-7 bg-gray-50" />
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Auto-linked to Slab 1 threshold.</p>
            </div>
            <NumField
              id="slab2-pct"
              label="Commission %"
              value={form.slab2_pct}
              onChange={(v) => set("slab2_pct", v)}
              step="0.1"
              min="0"
            />
          </div>
        </div>
      </div>

      <div className="p-5 rounded-2xl bg-[#FEF3E6] border border-[#F48E2E]/30" data-testid="fees-preview">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#F48E2E] font-bold mb-1">Preview</div>
            <div className="font-display text-xl">See what a vendor takes home</div>
          </div>
          <div className="w-40">
            <Label htmlFor="preview-gross" className="text-xs">Order gross</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
              <Input id="preview-gross" data-testid="preview-gross" type="number" min="0" value={preview} onChange={(e) => setPreview(e.target.value)} className="pl-7" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
          <div><div className="text-xs text-gray-500">Slab applied</div><div className="font-mono-tab font-semibold">{pct}%</div></div>
          <div><div className="text-xs text-gray-500">Commission</div><div className="font-mono-tab">₹{commission.toLocaleString("en-IN")}</div></div>
          <div><div className="text-xs text-gray-500">GST on commission</div><div className="font-mono-tab">{form.gst_on_commission ? `₹${gst.toLocaleString("en-IN")}` : "—"}</div></div>
          <div><div className="text-xs text-gray-500">Platform fee <span className="text-[10px] text-gray-400">(buyer)</span></div><div className="font-mono-tab">₹{platformFee.toLocaleString("en-IN")}</div></div>
          <div><div className="text-xs text-gray-500">Vendor receives</div><div className="font-display text-xl">₹{net.toLocaleString("en-IN")}</div></div>
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end gap-2">
        <Button
          data-testid="fees-save"
          onClick={save}
          disabled={!dirty || busy}
          className="rounded-full bg-[#F48E2E] hover:bg-[#D97815] text-white shadow-lg"
        >
          {busy ? "Saving…" : dirty ? "Save changes" : "No changes"}
        </Button>
      </div>
    </div>
  );
}
